import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, User, Calendar, Trophy, MapPin, Clock } from 'lucide-react';
import { ttStatsAPI } from '../../services/api';

interface Match {
  date: string;
  time: string;
  league: string;
  home: string;
  guest: string;
  result?: string;
  href?: string;
}

interface ClubTeam {
  name: string;
  league: string;
  href: string;
  captain?: string;
  rank?: string;
  points?: string;
}

interface ClubPlayer {
  classification: string;
  name: string;
  href: string;
  licenseNumber: string;
  series: string;
  nationality: string;
}

interface Venue {
  id: string;
  name: string;
  address: string;
  phone?: string;
  website?: string;
  routePlanner?: string;
}

interface UnifiedClubWidgetProps {
  clubId: string;
  theme?: 'light' | 'dark';
  defaultTab?: 'overview' | 'matches' | 'teams' | 'players' | 'venues';
  lang?: 'en' | 'fr' | 'de' | 'it';
  compact?: boolean;
  // Optional pre-loaded data to avoid duplicate API calls
  initialClubData?: any;
  initialTeams?: ClubTeam[];
  initialPlayers?: ClubPlayer[];
}

const UnifiedClubWidget: React.FC<UnifiedClubWidgetProps> = ({
  clubId,
  theme = 'light',
  defaultTab = 'overview',
  lang = 'en',
  compact = false,
  initialClubData,
  initialTeams,
  initialPlayers
}) => {
  const [clubData, setClubData] = useState<any>(initialClubData || null);
  const [teams, setTeams] = useState<ClubTeam[]>(initialTeams || []);
  const [players, setPlayers] = useState<ClubPlayer[]>(initialPlayers || []);
  const [loading, setLoading] = useState(!initialClubData);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [showAllMatches, setShowAllMatches] = useState(false);
  const [showAllTeams, setShowAllTeams] = useState(false);
  const [showAllPlayers, setShowAllPlayers] = useState(false);
  const [showAllVenues, setShowAllVenues] = useState(false);
  const isDark = theme === 'dark';
  const maxMatches = 5;
  const maxTeams = 6;
  const maxPlayers = 12;
  const maxVenues = 3;

  const translations = {
    en: {
      overview: 'Overview',
      matches: 'Matches',
      teams: 'Teams',
      players: 'Players',
      venues: 'Venues',
      showMore: 'Show more',
      showLess: 'Show less',
      noMatches: 'No upcoming matches',
      captain: 'Captain',
      upcomingMatches: 'Upcoming Matches',
      topTeams: 'Top Teams',
      viewAll: 'View All'
    },
    fr: {
      overview: 'Aperçu',
      matches: 'Matchs',
      teams: 'Équipes',
      players: 'Joueurs',
      venues: 'Lieux',
      showMore: 'Afficher plus',
      showLess: 'Afficher moins',
      noMatches: 'Aucun match à venir',
      captain: 'Capitaine',
      upcomingMatches: 'Prochains matchs',
      topTeams: 'Meilleures équipes',
      viewAll: 'Tout voir'
    },
    de: {
      overview: 'Übersicht',
      matches: 'Spiele',
      teams: 'Teams',
      players: 'Spieler',
      venues: 'Orte',
      showMore: 'Mehr anzeigen',
      showLess: 'Weniger anzeigen',
      noMatches: 'Keine anstehenden Spiele',
      captain: 'Kapitän',
      upcomingMatches: 'Kommende Spiele',
      topTeams: 'Top Teams',
      viewAll: 'Alle anzeigen'
    },
    it: {
      overview: 'Panoramica',
      matches: 'Partite',
      teams: 'Squadre',
      players: 'Giocatori',
      venues: 'Luoghi',
      showMore: 'Mostra di più',
      showLess: 'Mostra di meno',
      noMatches: 'Nessuna partita in programma',
      captain: 'Capitano',
      upcomingMatches: 'Prossime partite',
      topTeams: 'Migliori squadre',
      viewAll: 'Vedi tutto'
    }
  };

  const t = translations[lang];

  useEffect(() => {
    // Skip fetching if data is already provided
    if (initialClubData) {
      return;
    }

    const fetchData = async () => {
      try {
        const [clubInfo, clubTeams, clubPlayers] = await Promise.all([
          ttStatsAPI.getClubInfo(clubId),
          ttStatsAPI.getClubTeams(clubId),
          ttStatsAPI.getClubPlayers(clubId)
        ]);

        if (clubInfo && clubInfo.name) {
          clubInfo.name = clubInfo.name.replace(/^Swiss Table Tennis\s+/i, '').trim();
        }

        setClubData(clubInfo);
        setTeams(clubTeams.teams || []);
        setPlayers(clubPlayers.players || []);
      } catch (error) {
        console.error('Failed to fetch club data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  if (loading) {
    return (
      <div className={`w-full ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'} shadow-sm overflow-hidden font-sans`}>
        <div className="animate-pulse">
          {/* Header skeleton - matches actual header */}
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} px-4 py-3 border-b`}>
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <div className={`h-4 w-32 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded mb-1.5`} />
                <div className={`h-3 w-24 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
              </div>
              <div className={`flex items-center gap-2 h-7 px-3 ${isDark ? 'bg-gray-700' : 'bg-gray-100'} rounded-full ml-3`}>
                <div className={`h-4 w-4 ${isDark ? 'bg-gray-600' : 'bg-gray-200'} rounded`} />
                <div className={`h-3 w-16 ${isDark ? 'bg-gray-600' : 'bg-gray-200'} rounded`} />
              </div>
            </div>
          </div>

          {/* Tabs skeleton - matches actual tabs */}
          <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b px-3 py-2.5`}>
            <div className={`flex gap-1.5 ${isDark ? 'bg-gray-900' : 'bg-gray-100'} p-1 rounded-full`}>
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className={`h-7 ${i === 1 ? 'w-20' : 'w-24'} ${i === 1 ? (isDark ? 'bg-gray-700' : 'bg-white shadow-sm') : 'bg-transparent'} rounded-full`} />
              ))}
            </div>
          </div>

          {/* Content skeleton - matches overview tab with stats grid */}
          <div className="p-4 space-y-4">
            {/* Quick Stats grid */}
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3`}>
                  <div className={`h-5 w-5 mx-auto mb-1 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
                  <div className={`h-6 w-8 mx-auto mb-1 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
                  <div className={`h-3 w-12 mx-auto ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
                </div>
              ))}
            </div>

            {/* Section title */}
            <div className={`h-4 w-32 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />

            {/* Match-like items */}
            {[1, 2, 3].map((i) => (
              <div key={i} className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} rounded-lg p-3 border`}>
                <div className="flex justify-between items-center">
                  <div className="flex-1 space-y-2">
                    <div className={`h-3.5 w-48 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
                    <div className={`h-3 w-32 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
                  </div>
                  <div className={`h-3.5 w-12 ${isDark ? 'bg-gray-700' : 'bg-gray-200'} rounded`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const allMatches = clubData?.nextMatches?.flatMap((dateGroup: any) =>
    dateGroup.games || []
  ) || [];
  const upcomingMatches = showAllMatches ? allMatches : allMatches.slice(0, maxMatches);

  const clubName = clubData?.name || 'Club';
  const venues = clubData?.venues || [];
  const displayedTeams = showAllTeams ? teams : teams.slice(0, maxTeams);
  const displayedPlayers = showAllPlayers ? players : players.slice(0, maxPlayers);
  const displayedVenues = showAllVenues ? venues : venues.slice(0, maxVenues);
  const topTeams = teams.slice(0, 3);

  const tabs = [
    { id: 'overview', label: t.overview, icon: Trophy },
    { id: 'matches', label: t.matches, icon: Calendar, count: allMatches.length },
    { id: 'teams', label: t.teams, icon: Users, count: teams.length },
    { id: 'players', label: t.players, icon: User, count: players.length },
    { id: 'venues', label: t.venues, icon: MapPin, count: venues.length }
  ];

  return (
    <div className={`w-full ${isDark ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'} rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'} shadow-sm overflow-hidden font-sans transition-colors duration-300`}>
      {/* Header with TTStats branding */}
      <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} px-4 py-3 border-b transition-colors duration-300`}>
        <div className="flex items-center justify-between">
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold text-sm truncate ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>{clubName}</h3>
            <p className={`text-xs mt-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Swiss Table Tennis</p>
          </div>
          <a
            href={`https://ttstats.ch/${lang}/club/${clubId}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`flex items-center gap-2 text-xs font-medium px-3 py-1.5 rounded-full ${isDark ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} transition-colors whitespace-nowrap ml-3`}
          >
            <img
              src="/app-icon.png"
              alt="TTStats"
              className="h-4 w-4 rounded"
            />
            TTStats.ch
          </a>
        </div>
      </div>

      {/* Tabs */}
      {!compact && (
        <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border-b px-3 py-2.5 transition-colors duration-300`}>
          <div className={`flex gap-1.5 overflow-x-auto ${isDark ? 'bg-gray-900' : 'bg-gray-100'} p-1 rounded-full`}>
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`
                    relative flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all whitespace-nowrap
                    ${activeTab === tab.id
                      ? isDark ? 'bg-gray-700 text-white shadow-sm' : 'bg-white text-gray-900 shadow-sm'
                      : isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'
                    }
                  `}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <span className={`text-xs ${
                      activeTab === tab.id
                        ? isDark ? 'text-gray-300' : 'text-gray-500'
                        : isDark ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-4">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3 text-center`}>
                <Users className={`h-5 w-5 mx-auto mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {teams.length}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.teams}</div>
              </div>
              <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3 text-center`}>
                <User className={`h-5 w-5 mx-auto mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {players.length}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.players}</div>
              </div>
              <div className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3 text-center`}>
                <Calendar className={`h-5 w-5 mx-auto mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                <div className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {allMatches.length}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{t.matches}</div>
              </div>
            </div>

            {/* Top Teams */}
            {topTeams.length > 0 && (
              <div>
                <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  <Trophy className="h-4 w-4" />
                  Top Teams
                </h4>
                <div className="space-y-2">
                  {topTeams.map((team, idx) => (
                    <div
                      key={idx}
                      className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-2 flex items-center justify-between`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className={`text-sm font-bold w-6 text-center ${
                          team.rank === '1' ? 'text-yellow-600' :
                          team.rank === '2' ? 'text-gray-600' :
                          team.rank === '3' ? 'text-orange-600' :
                          isDark ? 'text-gray-400' : 'text-gray-700'
                        }`}>
                          #{team.rank || idx + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-medium truncate">{team.name}</div>
                          <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{team.league}</div>
                        </div>
                      </div>
                      {team.points && (
                        <div className={`text-xs font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                          {team.points}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Next Match */}
            {upcomingMatches.length > 0 && (
              <div>
                <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  <Calendar className="h-4 w-4" />
                  Next Match
                </h4>
                {(() => {
                  const match = upcomingMatches[0];
                  const isHomeTeam = match.home.includes(clubName);
                  const isAwayTeam = match.guest.includes(clubName);

                  return (
                    <div className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-3`}>
                      <div className="flex items-center gap-2 mb-2 text-xs">
                        <Clock className="h-3 w-3 opacity-60" />
                        <span className="font-medium">{match.time}</span>
                        <span className={`ml-auto px-2 py-0.5 rounded-full text-xs ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          {match.league}
                        </span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <div className={`flex-1 text-right ${isHomeTeam ? 'font-semibold' : ''}`}>
                          {match.home}
                        </div>
                        <div className={`px-2 py-0.5 mx-2 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-200'} font-medium opacity-60`}>
                          VS
                        </div>
                        <div className={`flex-1 text-left ${isAwayTeam ? 'font-semibold' : ''}`}>
                          {match.guest}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Venues */}
            {clubData?.venues && clubData.venues.length > 0 && (
              <div>
                <h4 className={`text-sm font-semibold mb-2 flex items-center gap-2 ${isDark ? 'text-gray-200' : 'text-gray-900'}`}>
                  <MapPin className="h-4 w-4" />
                  Venues
                </h4>
                <div className="space-y-2">
                  {clubData.venues.map((venue: Venue, idx: number) => (
                    <div
                      key={venue.id}
                      className={`${isDark ? 'bg-gray-800' : 'bg-gray-50'} rounded-lg p-2`}
                    >
                      <div className="text-xs font-medium">{venue.name}</div>
                      <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{venue.address}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* Matches Tab - Reuse existing logic */}
        {activeTab === 'matches' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            {upcomingMatches.length > 0 ? (
              upcomingMatches.map((match: Match, index: number) => {
                const isHomeTeam = match.home.includes(clubName);
                const isAwayTeam = match.guest.includes(clubName);

                return (
                  <div
                    key={index}
                    className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3 relative`}
                  >
                    <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
                      isHomeTeam ? 'bg-blue-500' : isAwayTeam ? 'bg-purple-500' : 'bg-gray-300'
                    }`} />
                    <div className="pl-2">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-1.5 text-xs">
                          <Clock className="h-3 w-3 opacity-60" />
                          <span className="font-medium">{match.time}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
                          {match.league}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2 text-xs">
                        <div className={`flex-1 text-right ${isHomeTeam ? 'font-semibold' : ''}`}>
                          {match.home}
                        </div>
                        <div className={`px-2 py-0.5 rounded ${isDark ? 'bg-gray-700' : 'bg-gray-200'} font-medium opacity-60`}>
                          VS
                        </div>
                        <div className={`flex-1 text-left ${isAwayTeam ? 'font-semibold' : ''}`}>
                          {match.guest}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t.noMatches}</p>
              </div>
            )}
            {allMatches.length > maxMatches && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAllMatches(!showAllMatches)}
                  className={`w-full py-2 text-sm font-medium rounded transition-colors ${
                    isDark
                      ? 'text-blue-400 hover:text-blue-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-blue-600 hover:text-blue-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {showAllMatches ? t.showLess : t.showMore}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Teams Tab */}
        {activeTab === 'teams' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            {displayedTeams.map((team, index) => (
              <div
                key={index}
                className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-3 relative`}
              >
                {team.rank && (
                  <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${
                    team.rank === '1' ? 'bg-yellow-500' :
                    team.rank === '2' ? 'bg-gray-400' :
                    team.rank === '3' ? 'bg-orange-500' :
                    isDark ? 'bg-gray-600' : 'bg-gray-300'
                  }`} />
                )}
                <div className="pl-2 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <h4 className="text-sm font-medium truncate">{team.name}</h4>
                      <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {team.league}
                      </span>
                    </div>
                    {team.captain && (
                      <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t.captain}: {team.captain}
                      </p>
                    )}
                  </div>
                  {(team.rank || team.points) && (
                    <div className="flex items-center gap-2 ml-2">
                      {team.rank && (
                        <div className={`text-lg font-semibold ${
                          team.rank === '1' ? 'text-yellow-600' :
                          team.rank === '2' ? 'text-gray-600' :
                          team.rank === '3' ? 'text-orange-600' :
                          isDark ? 'text-gray-400' : 'text-gray-700'
                        }`}>
                          #{team.rank}
                        </div>
                      )}
                      {team.points && (
                        <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {team.points}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {teams.length > maxTeams && (
              <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAllTeams(!showAllTeams)}
                  className={`w-full py-2 text-sm font-medium rounded transition-colors ${
                    isDark
                      ? 'text-blue-400 hover:text-blue-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-blue-600 hover:text-blue-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {showAllTeams ? t.showLess : t.showMore}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Players Tab */}
        {activeTab === 'players' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            <div className="grid grid-cols-2 gap-2">
            {displayedPlayers.map((player) => (
              <div
                key={player.licenseNumber}
                className={`${isDark ? 'bg-gray-800 border-gray-700' : 'bg-gray-50 border-gray-200'} border rounded-lg p-2`}
              >
                <div
                  className="inline-block px-1.5 py-0.5 rounded text-xs font-bold mb-1 text-white"
                  style={{
                    backgroundColor: player.classification.startsWith('A') ? '#1A1A1A' :
                      player.classification.startsWith('B') ? '#9C27B0' :
                      player.classification.startsWith('C') ? '#F59E0B' :
                      '#1C7AD6'
                  }}
                >
                  {player.classification}
                </div>
                <div className="text-xs font-medium truncate">
                  {player.name}
                </div>
                <div className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                  {player.series}
                </div>
              </div>
            ))}
            </div>
            {players.length > maxPlayers && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAllPlayers(!showAllPlayers)}
                  className={`w-full py-2 text-sm font-medium rounded transition-colors ${
                    isDark
                      ? 'text-blue-400 hover:text-blue-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-blue-600 hover:text-blue-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {showAllPlayers ? t.showLess : t.showMore}
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Venues Tab */}
        {activeTab === 'venues' && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-2"
          >
            {displayedVenues.length > 0 ? (
              displayedVenues.map((venue: Venue, index: number) => (
                <a
                  key={venue.id}
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(venue.address)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`block ${isDark ? 'bg-gray-800 border-gray-700 hover:bg-gray-750' : 'bg-gray-50 border-gray-200 hover:bg-gray-100'} border rounded-lg p-3 transition-colors cursor-pointer`}
                >
                  <div className="flex items-start gap-2">
                    <MapPin className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                    <div className="flex-1">
                      <h4 className="text-sm font-medium mb-1">{venue.name}</h4>
                      <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        {venue.address}
                      </p>
                      {venue.phone && (
                        <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                          {venue.phone}
                        </p>
                      )}
                    </div>
                  </div>
                </a>
              ))
            ) : (
              <div className={`text-center py-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <MapPin className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No venues found</p>
              </div>
            )}
            {venues.length > maxVenues && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => setShowAllVenues(!showAllVenues)}
                  className={`w-full py-2 text-sm font-medium rounded transition-colors ${
                    isDark
                      ? 'text-blue-400 hover:text-blue-300 bg-gray-700 hover:bg-gray-600'
                      : 'text-blue-600 hover:text-blue-700 bg-white hover:bg-gray-50'
                  }`}
                >
                  {showAllVenues ? t.showLess : t.showMore}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default UnifiedClubWidget;
